export const CURRENT_CONFIG_VERSION = 6

/**
 * Maps v1 calculation types to the v2 column types they expand into.
 * Single-column types map 1:1. Multi-column types expand into individual entries.
 * The first entry in each array is the "primary" field.
 */
const V1_TYPE_TO_COLUMNS = {
  marketValue: ['marketValue'],
  performance: ['performance'],
  costBasis: ['costBasis', 'costBasis:gainLoss'],
  estimatedIncome: ['estimatedIncome'],
  performanceContribution: ['performanceContribution', 'performanceContribution:avgWeight'],
  shorttermCostBasis: ['shorttermCostBasis', 'shorttermCostBasis:gainLoss'],
  longtermCostBasis: ['longtermCostBasis', 'longtermCostBasis:gainLoss'],
  benchmarkPerformance: ['benchmarkPerformance'],
  benchmarkAftertaxPerformance: ['benchmarkAftertaxPerformance'],
  attributionEffect: [
    'attributionEffect:allocation',
    'attributionEffect:selection',
    'attributionEffect:interaction',
    'attributionEffect:total'
  ],
  yield: ['yield'],
  currentYield: ['currentYield'],
  yieldOnCost: ['yieldOnCost'],
  yieldToMaturity: ['yieldToMaturity'],
  yieldToNextCall: ['yieldToNextCall'],
  yieldToWorst: ['yieldToWorst'],
  groupStatistic: ['groupStatistic'],
  benchmarkStatistic: ['benchmarkStatistic'],
  taxAlpha: ['taxAlpha'],
  taxSavings: ['taxSavings'],
  targetAllocation: [
    'targetAllocation:target',
    'targetAllocation:actual',
    'targetAllocation:drift'
  ],
  aip: [
    'aip:committedCapital',
    'aip:contributedCapital',
    'aip:unfundedCommitment',
    'aip:unfundedAdj',
    'aip:capCall',
    'aip:cashDist',
    'aip:recallDist'
  ]
}

const AIP_FLAG_TO_COLUMN = {
  showCommitted: 'aip:committedCapital',
  showContributed: 'aip:contributedCapital',
  showUnfunded: 'aip:unfundedCommitment',
  showUnfundedAdj: 'aip:unfundedAdj',
  showCapCall: 'aip:capCall',
  showCashDist: 'aip:cashDist',
  showRecallDist: 'aip:recallDist'
}

const AIP_SHOW_FLAGS = new Set(Object.keys(AIP_FLAG_TO_COLUMN))

const AIP_FLAG_DEFAULTS = {
  showCommitted: true,
  showContributed: true,
  showUnfunded: true,
  showUnfundedAdj: false,
  showCapCall: false,
  showCashDist: false,
  showRecallDist: false
}

function migrateV1Column (col) {
  const { type, options } = col

  if (type === 'aip') {
    const cleanOptions = {}
    if (options) {
      for (const [k, v] of Object.entries(options)) {
        if (!AIP_SHOW_FLAGS.has(k)) cleanOptions[k] = v
      }
    }

    const enabledColumns = []
    for (const [flag, columnType] of Object.entries(AIP_FLAG_TO_COLUMN)) {
      const isEnabled = options?.[flag] ?? AIP_FLAG_DEFAULTS[flag]
      if (isEnabled) enabledColumns.push(columnType)
    }

    if (enabledColumns.length === 0) {
      enabledColumns.push('aip:committedCapital')
    }

    return enabledColumns.map(columnType => ({
      columnType,
      label: null,
      options: cleanOptions
    }))
  }

  const columnTypes = V1_TYPE_TO_COLUMNS[type]
  if (!columnTypes) return []

  const cleanOptions = options ? { ...options } : {}

  return columnTypes.map(columnType => ({
    columnType,
    label: null,
    options: cleanOptions
  }))
}

/**
 * Migrates a saved configuration blob from an older version to the current version.
 * Each version bump adds a migration step that applies defaults for new fields.
 * The server stores configVersion alongside the blob so this function can
 * incrementally upgrade any saved config to the latest shape.
 *
 * This is the single point of responsibility for backfilling defaults on older
 * saved configs. We intentionally do NOT merge with DEFAULT_CONFIG on load —
 * that would silently mask missing fields and make it unclear which version
 * introduced them. Instead, when a new field is added to the config schema:
 *   1. Bump CURRENT_CONFIG_VERSION
 *   2. Add a migration step below that sets the new field's default
 *   3. The reducer's LOAD_CONFIG case will receive the fully migrated object
 */
export function migrateConfig (config, fromVersion) {
  const result = { ...config }

  if (fromVersion < 2) {
    if (Array.isArray(result.columns)) {
      result.columns = result.columns.flatMap(col => {
        if (col.columnType) return [col]
        return migrateV1Column(col)
      })
    }
  }

  if (fromVersion < 3) {
    if (Array.isArray(result.columns)) {
      result.columns = result.columns.map(col => {
        if (col.columnType?.startsWith('derived:') && col.options && col.options.inputColumnIds == null) {
          return { ...col, options: { ...col.options, inputColumnIds: {} } }
        }
        return col
      })
    }
  }

  if (fromVersion < 4) {
    if (Array.isArray(result.columns)) {
      result.columns = result.columns.map(col => {
        if (!col.columnType?.startsWith('derived:') || !col.options?.inputColumnIds) return col
        const { inputColumnIds, ...rest } = col.options
        return { ...col, options: rest }
      })
    }
  }

  if (fromVersion < 5) {
    if (result.includeBenchmarks == null) {
      result.includeBenchmarks = false
    }
  }

  if (fromVersion < 6) {
    // SMT-1608 Phase 1: per-grouping scope filters. Old saved configs
    // didn't have this field; backfill with an empty map so the reducer
    // doesn't have to handle undefined and the FilterBuilder can address
    // grouping keys directly.
    if (result.scopeFilters == null) {
      result.scopeFilters = {}
    }
  }

  // Note: derived:mvChange (added in SMT-1612, no version bump) seeds
  // `activityTypes` via `catalog.defaultOptions` only at column creation
  // time (createColumnInstance). Hand-edited or older saved configs that
  // reference `derived:mvChange` without `options.activityTypes` will
  // produce a column that renders `--` (empty selection = no value, by
  // design). When we next bump to v6, add a migration step that backfills
  // any `derived:mvChange` column missing `activityTypes` with
  // `MV_CHANGE_DEFAULT_ACTIVITY_TYPES` from constants.js.

  return result
}
